import { useQuery } from '@apollo/client';
import React from 'react';
import RoutePath from 'App/RoutePath';
import PageCard from 'Shared/PageCard/PageCard';
import SectionError from 'Shared/SectionError';
import { Namespace } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import { UserFeature } from '__generated__/globalTypes';
import { calculateSupportFeatureCount } from '../utils';
import HomeSectionSkeleton from '../components/HomeSectionSkeleton';
import { useSectionErrorLogging } from '../hooks/useSectionErrorLogging';
import { HOME_SECTION_QUERY_OPTIONS } from '../sectionQueryOptions';
import Coaching from './assets/therapy_coaching.png';
import Tools from './assets/wellbeing_tools.png';
import Helplines from './assets/helplines.png';
import Managers from './assets/managers_training.png';
import { HomeSupportForYou } from './__generated__/HomeSupportForYou';
import { HOME_SUPPORT_FOR_YOU_QUERY } from './supportForYouQueries';
import './SupportForYou.scss';

/**
 * Feature shortcut cards, one per entitlement. Hidden below two cards, since a
 * single shortcut does not warrant a section.
 */
const SupportForYou = () => {
  const { t: translate } = useTranslation<Namespace<'homepage'>>('homepage');
  const { entitlements, loading: isEntitlementsLoading } =
    useUserEntitlements();

  const { data, loading, error, refetch } = useQuery<HomeSupportForYou>(
    HOME_SUPPORT_FOR_YOU_QUERY,
    HOME_SECTION_QUERY_OPTIONS,
  );

  useSectionErrorLogging(error, data, 'support-for-you');

  if ((loading && !data) || isEntitlementsLoading) {
    return (
      <HomeSectionSkeleton testId="support-for-you-skeleton" tileCount={3} />
    );
  }

  if (error && !data) {
    // `errorPolicy: 'none'` makes refetch() reject on failure; the hook's own
    // `data` and `error` still drive what renders.
    return (
      <SectionError onRetry={async () => refetch().catch(() => undefined)} />
    );
  }

  const userFeatures = data?.user?.features;

  if (!userFeatures) {
    return null;
  }

  const isManagerExperienceActive = entitlements.lineManagerContent;
  const supportFeatureCount = calculateSupportFeatureCount(
    isManagerExperienceActive,
    userFeatures,
  );

  if (supportFeatureCount < 2) {
    return null;
  }

  const cardCount = supportFeatureCount >= 3 ? supportFeatureCount : 3;

  return (
    <section>
      <div
        className="support-cards"
        style={{ '--support-card-count': cardCount } as React.CSSProperties}
      >
        {userFeatures.includes(UserFeature.TALK) && (
          <PageCard
            title={translate('support_for_you_section.therapy_card_title')}
            imageSrc={Coaching}
            to={RoutePath.Book}
          />
        )}

        <PageCard
          title={translate('support_for_you_section.wellbeing_card_title')}
          imageSrc={Tools}
          to={RoutePath.Explore}
        />

        {userFeatures.includes(UserFeature.HELP) && (
          <PageCard
            title={translate('support_for_you_section.help_card_title')}
            imageSrc={Helplines}
            to={RoutePath.Help}
          />
        )}

        {userFeatures.includes(UserFeature.LINE_MANAGER_CONTENT) &&
          isManagerExperienceActive && (
            <PageCard
              title={translate('support_for_you_section.managers_card_title')}
              imageSrc={Managers}
              to={RoutePath.Managers}
            />
          )}
      </div>
    </section>
  );
};

export default SupportForYou;
